import { useState } from "react";
import { MessageSquareText, Send, Loader2 } from "lucide-react";
import { submitLead, api } from "../../lib/api";
import { siteConfig } from "../../config";
import StepShell from "./StepShell";

/**
 * Step 9 — Submit.
 *
 * Final contact-info capture + send. Guests can BUILD with only a first
 * name, but submission requires the full contact set. Buttons:
 *   • Send My Deal to Scott  → POST /api/leads (existing endpoint)
 *   • Text Scott Now         → opens native SMS prefilled w/ deal summary
 *
 * Strict guardrails:
 *   • Math + payment summary are precomputed by ReviewStep / wizard. We
 *     just forward the resulting payload — we never recompute fees, tax,
 *     or rebate stacks here.
 */
export default function SubmitStep({ byod, vehicle, dealMath, onBack, firstName }) {
  const c = byod.state.contact || {};
  const setC = (patch) => byod.update("contact", patch);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr("");
    if (!c.first_name?.trim() || !c.last_name?.trim()) return setErr("First and last name are required to submit your deal.");
    if (!c.phone?.trim() && !c.email?.trim()) return setErr("Please share a phone or email so Scott can reach you.");

    setBusy(true);
    try {
      const payload = buildLeadPayload({ byod, vehicle, dealMath });
      const res = await submitLead(payload);
      if (res?.ok) {
        setDone(true);
        // Best-effort: nuke the local draft now that it's been sent
        try {
          await api.delete("/byod/draft");
        } catch { /* ignore */ }
      } else {
        setErr("Something went wrong. Please try again or text Scott directly.");
      }
    } catch (ex) {
      setErr("Something went wrong. Please try again, or text Scott directly for help.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <StepShell
        title={`Thanks${firstName ? `, ${firstName}` : ""} — Scott has your deal.`}
        subtitle="He personally reviews every submission and will reach out using the contact method you picked."
        showBack={false}
        showContinue={false}
        testIdPrefix="byod-submit-done"
      >
        <div className="rounded-2xl border-2 border-emerald-500 bg-emerald-50/40 p-5">
          <div className="font-extrabold text-emerald-800">Sent successfully.</div>
          <p className="text-sm text-[var(--ssc-ink)] mt-1">
            We saved a copy of this deal on your account. Scott usually responds within a few business hours.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <a href={siteConfig.scott.phoneSms} className="ssc-btn ssc-btn-primary" data-testid="byod-submit-done-text-scott">
              <MessageSquareText size={14} /> Text Scott
            </a>
            <a href="/" className="ssc-btn ssc-btn-outline" data-testid="byod-submit-done-home">Back to home</a>
          </div>
        </div>
      </StepShell>
    );
  }

  const sms = buildSmsBody({ byod, vehicle, dealMath });

  return (
    <StepShell
      title={firstName ? `${firstName}, ready to send this to Scott?` : "Ready to send this to Scott?"}
      subtitle="A few last details so Scott can reach you — then he takes it from here."
      onBack={onBack}
      showContinue={false}
      testIdPrefix="byod-submit-step"
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="First name *">
            <input className="ssc-input" value={c.first_name || ""} onChange={(e) => setC({ first_name: e.target.value })} autoComplete="given-name" data-testid="byod-submit-firstname" />
          </Field>
          <Field label="Last name *">
            <input className="ssc-input" value={c.last_name || ""} onChange={(e) => setC({ last_name: e.target.value })} autoComplete="family-name" data-testid="byod-submit-lastname" />
          </Field>
          <Field label="Phone">
            <input className="ssc-input" value={c.phone || ""} onChange={(e) => setC({ phone: e.target.value })} inputMode="tel" autoComplete="tel" data-testid="byod-submit-phone" />
          </Field>
          <Field label="Email">
            <input className="ssc-input" type="email" value={c.email || ""} onChange={(e) => setC({ email: e.target.value })} autoComplete="email" data-testid="byod-submit-email" />
          </Field>
          <Field label="Preferred contact method">
            <select className="ssc-select" value={c.preferred_contact || "Text"} onChange={(e) => setC({ preferred_contact: e.target.value })} data-testid="byod-submit-preferred">
              <option>Text</option>
              <option>Call</option>
              <option>Email</option>
            </select>
          </Field>
          <Field label="Best time to contact">
            <input className="ssc-input" value={c.best_time || ""} onChange={(e) => setC({ best_time: e.target.value })} placeholder="e.g. Weekdays after 5pm" data-testid="byod-submit-besttime" />
          </Field>
          <Field label="Notes for Scott" full>
            <textarea className="ssc-textarea" rows={3} value={c.notes || ""} onChange={(e) => setC({ notes: e.target.value })} placeholder="Anything else Scott should know?" data-testid="byod-submit-notes" />
          </Field>
        </div>

        {err && <div className="text-sm text-[var(--ssc-red)]" data-testid="byod-submit-error">{err}</div>}

        <div className="flex flex-wrap gap-2 items-center justify-between pt-2">
          <a
            href={`sms:+16145358673&body=${encodeURIComponent(sms)}`}
            className="ssc-btn ssc-btn-outline"
            data-testid="byod-submit-text-scott"
          >
            <MessageSquareText size={16} /> Text Scott Now
          </a>
          <button
            type="submit"
            disabled={busy}
            className="ssc-btn ssc-btn-primary"
            data-testid="byod-submit-send"
          >
            {busy ? <><Loader2 size={16} className="animate-spin" /> Sending...</> : <><Send size={16} /> Send My Deal to Scott</>}
          </button>
        </div>
      </form>
    </StepShell>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="ssc-label">{label}</span>
      {children}
    </label>
  );
}

function buildSmsBody({ byod, vehicle, dealMath }) {
  const c = byod.state.contact || {};
  const title = vehicle ? `${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.trim || ""}`.trim() : "the Chevy";
  const monthly = dealMath?.estimatedLow ? `~$${Math.round(dealMath.estimatedLow).toLocaleString()}/mo` : "";
  return `Hey Scott — I just built a deal on the ${title}${vehicle?.stock_number ? ` (stock ${vehicle.stock_number})` : ""}: ${monthly}. ${c.first_name || ""}`.trim();
}

function buildLeadPayload({ byod, vehicle, dealMath }) {
  const s = byod.state;
  const c = s.contact || {};
  const trade = s.trade || {};
  const rebates = s.rebates || {};
  const rate = s.rate || {};
  const reqProt = s.required_protection?.states || {};
  const optProt = s.optional_protection?.states || {};
  const fullName = `${(c.first_name || "").trim()} ${(c.last_name || "").trim()}`.trim();
  const detailHref = vehicle ? `/${vehicle.status}-vehicles/${vehicle.slug}` : null;
  return {
    // Required-ish base
    name: fullName,
    phone: c.phone || "",
    email: c.email || "",
    preferred_contact: c.preferred_contact || "Text",
    message: c.notes || "",
    best_time_to_contact: c.best_time || "",
    lead_type: "build_my_own_deal",
    lead_source: "Build My Own Deal",
    form_name: "Build My Own Deal",
    cta_clicked: "Send My Deal to Scott",
    // Vehicle prefill (DO NOT modify routing per project guardrails — read-only)
    vehicle_title: vehicle ? `${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.trim || ""}`.trim() : null,
    vin: vehicle?.vin || null,
    stock_number: vehicle?.stock_number || null,
    new_or_used: vehicle?.status || null,
    year: vehicle?.year || null,
    make: vehicle?.make || null,
    model: vehicle?.model || null,
    trim: vehicle?.trim || null,
    advertised_price: vehicle?.price || null,
    vehicle_mileage: vehicle?.mileage ?? null,
    vdp_source: detailHref,
    // Identity / mode
    customer_account_mode: s.identity?.mode || "guest",
    // Trade
    trade_included: trade.has_trade === "yes",
    trade_status: trade.has_trade,
    trade_year: trade.year || null,
    trade_make: trade.make || null,
    trade_model: trade.model || null,
    trade_mileage: trade.mileage || null,
    trade_payoff: Number(trade.payoff || 0) || 0,
    trade_appraisal_low: Number(trade.appraisal_low || 0) || 0,
    trade_appraisal_high: Number(trade.appraisal_high || 0) || 0,
    trade_selected_value: trade.selected_value || null,
    trade_value: byod.tradeSelectedValue,
    // Money down
    money_down_choice: s.money_down?.choice || null,
    deal_down_payment: byod.moneyDownAmount,
    // Rebates
    selected_rebates: rebates.selected || [],
    not_sure_rebates: rebates.not_sure || [],
    skipped_rebates: rebates.skipped || [],
    total_rebate_savings_estimated: rebates.total_savings_estimated || 0,
    rebate_acknowledged_at: rebates.acknowledged ? new Date().toISOString() : null,
    // Rate / Special APR
    credit_tier: rate.credit_tier || null,
    selected_term: rate.term || null,
    using_override_apr: !!rate.special_apr_id,
    special_apr_id: rate.special_apr_id || null,
    special_apr_pct: rate.special_apr_pct ?? null,
    special_apr_term: rate.special_apr_term ?? null,
    special_apr_name: rate.special_apr_name || null,
    // Accessories
    accessories: s.accessories || [],
    accessories_total: byod.accessoriesTotal,
    accessory_site_interest: !!s.accessory_site_interest,
    // Required protection
    required_protection_accepted: Object.entries(reqProt)
      .filter(([, st]) => st?.status === "accepted")
      .map(([slug, st]) => ({ slug, price: st.finalPrice || 0 })),
    required_protection_declined: Object.entries(reqProt)
      .filter(([, st]) => st?.status === "declined")
      .map(([slug]) => slug),
    // Optional protection
    optional_protection_accepted: Object.entries(optProt)
      .filter(([, st]) => st?.status === "accepted")
      .map(([slug, st]) => ({ slug, price: st.finalPrice || 0 })),
    optional_protection_declined: Object.entries(optProt)
      .filter(([, st]) => st?.status === "declined")
      .map(([slug]) => slug),
    protection_total: byod.coveragesTotal,
    // Sales tax / county
    deal_county: s.sales_tax?.county || "Franklin",
    deal_county_tax_rate: dealMath?.countyRate ?? null,
    deal_sales_tax: dealMath?.tax ?? null,
    deal_taxable_amount: dealMath?.taxable ?? null,
    deal_doc_fee: 398,
    deal_state_fee: 18,
    deal_title_fee: 50,
    // Deal math (computed centrally by BuildMyOwnDeal)
    deal_estimated_amount_financed: dealMath?.amountFinanced ?? null,
    deal_estimated_monthly: dealMath?.estimatedMonthly ?? dealMath?.estimatedLow ?? null,
    // Back-compat fields (low/high are equal to monthly now that we only
    // show a single estimate). Kept for any existing downstream consumers.
    deal_estimated_monthly_low: dealMath?.estimatedLow ?? null,
    deal_estimated_monthly_high: dealMath?.estimatedHigh ?? null,
    deal_tax: dealMath?.tax ?? null,
    deal_fees: dealMath?.fees ?? null,
    estimate_acknowledged_at: s.review?.estimate_acknowledged_at || null,
    page_url: typeof window !== "undefined" ? window.location.href : "",
    submitted_at: new Date().toISOString(),
  };
}

export { buildLeadPayload, buildSmsBody };
